import { ColliType } from '@frontend/colli/types';
import { EquipmentStateName } from '@frontend/equipment/types';
import { PackageType } from '@frontend/package/types';
import { ProductType } from '@frontend/product/types';
import { TransactionType } from '@frontend/transaction/types';

export enum ConstraintType {
    ACCOUNT = 'account',
    APPLICATION = 'application',
    DESTINATION = 'destination',
    DIMENSION = 'dimension',
    EQUIPMENT = 'equipment',
    EQUIPMENT_GROUP = 'equipment_group',
    EQUIPMENT_STATE = 'equipment_state',
    FEATURE = 'feature',
    FUNCTIONALITY = 'functionality',
    LOCATION = 'location',
    PRODUCT = 'product',
    PRODUCT_GROUP = 'product_group',
    PRODUCT_TYPE = 'product_type',
    COLLI_TYPE = 'colli_type',
    PACKAGE_TYPE = 'package_type',
    STOCK = 'stock',
    TRANSACTION_COUNT = 'transaction_count',
    TRANSACTION_TYPE = 'transaction_type'
}

export type ConstraintData =
    | AccountConstraintData
    | ApplicationConstraintData
    | DestinationConstraintData
    | DimensionConstraintData
    | EquipmentConstraintData
    | EquipmentStateConstraintData
    | FeatureConstraintData
    | FunctionalityConstraintData
    | LocationConstraintData
    | ProductConstraintData
    | ProductTypeConstraintData
    | ColliTypeConstraintData
    | PackageTypeConstraintData
    | StockConstraintData
    | TransactionCountConstraintData
    | TransactionTypeConstraintData
    | ProductGroupConstraintData
    | EquipmentGroupConstraintData;

export const getConstraintDataType = (constraintData: any): ConstraintType | undefined => {
    if (isAccountConstraintData(constraintData)) {
        return ConstraintType.ACCOUNT;
    } else if (isDimensionConstraintData(constraintData)) {
        return ConstraintType.DIMENSION;
    } else if (isEquipmentConstraintData(constraintData)) {
        return ConstraintType.EQUIPMENT;
    } else if (isEquipmentStateConstraintData(constraintData)) {
        return ConstraintType.EQUIPMENT_STATE;
    } else if (isLocationConstraintData(constraintData)) {
        return ConstraintType.LOCATION;
    } else if (isProductConstraintData(constraintData)) {
        return ConstraintType.PRODUCT;
    } else if (isProductTypeConstraintData(constraintData)) {
        return ConstraintType.PRODUCT_TYPE;
    } else if (isColliTypeConstraintData(constraintData)) {
        return ConstraintType.COLLI_TYPE;
    } else if (isPackageTypeConstraintData(constraintData)) {
        return ConstraintType.PACKAGE_TYPE;
    } else if (isStockConstraintData(constraintData)) {
        return ConstraintType.STOCK;
    } else if (isTransactionCountConstraintData(constraintData)) {
        return ConstraintType.TRANSACTION_COUNT;
    } else if (isTransactionTypeConstraintData(constraintData)) {
        return ConstraintType.TRANSACTION_TYPE;
    } else if (isProductGroupConstraintData(constraintData)) {
        return ConstraintType.PRODUCT_GROUP;
    } else if (isEquipmentGroupConstraintData(constraintData)) {
        return ConstraintType.EQUIPMENT_GROUP;
    } else if (isApplicationConstraintData(constraintData)) {
        return ConstraintType.APPLICATION;
    } else if (isDestinationConstraintData(constraintData)) {
        return ConstraintType.DESTINATION;
    } else if (isFeatureConstraintData(constraintData)) {
        return ConstraintType.FEATURE;
    } else if (isFunctionalityConstraintData(constraintData)) {
        return ConstraintType.FUNCTIONALITY;
    }
    return undefined;
};

export const isAccountConstraintData = (constraintData: any): constraintData is AccountConstraintData => {
    return constraintData && (constraintData as AccountConstraintData).account_id !== undefined;
};

export interface AccountConstraintData {
    account_id?: string;
}

export const isDimensionConstraintData = (constraintData: any): constraintData is DimensionConstraintData => {
    return (
        constraintData &&
        (constraintData as DimensionConstraintData).height !== undefined &&
        (constraintData as DimensionConstraintData).width !== undefined &&
        (constraintData as DimensionConstraintData).depth !== undefined
    );
};
export interface DimensionConstraintData {
    height?: number;
    width?: number;
    depth?: number;
}

export const isEquipmentConstraintData = (constraintData: any): constraintData is EquipmentConstraintData => {
    return constraintData && (constraintData as EquipmentConstraintData).equipment_id !== undefined;
};
export interface EquipmentConstraintData {
    equipment_id?: string;
}

export const isEquipmentStateConstraintData = (constraintData: any): constraintData is EquipmentStateConstraintData => {
    return constraintData && (constraintData as EquipmentStateConstraintData).state !== undefined;
};
export interface EquipmentStateConstraintData {
    state?: EquipmentStateName;
}

export const isProductConstraintData = (constraintData: any): constraintData is ProductConstraintData => {
    return constraintData && (constraintData as ProductConstraintData).product_id !== undefined;
};

export interface ProductConstraintData {
    product_id?: string;
}

export const isProductTypeConstraintData = (constraintData: any): constraintData is ProductTypeConstraintData => {
    return constraintData && (constraintData as ProductTypeConstraintData).type !== undefined;
};
export interface ProductTypeConstraintData {
    type?: ProductType;
}

export const isColliTypeConstraintData = (constraintData: any): constraintData is ColliTypeConstraintData => {
    return constraintData && (constraintData as ColliTypeConstraintData).type !== undefined;
};
export interface ColliTypeConstraintData {
    type?: ColliType;
}

export const isPackageTypeConstraintData = (constraintData: any): constraintData is PackageTypeConstraintData => {
    return constraintData && (constraintData as PackageTypeConstraintData).type !== undefined;
};
export interface PackageTypeConstraintData {
    type?: PackageType;
}

export const isStockConstraintData = (constraintData: any): constraintData is StockConstraintData => {
    return constraintData && (constraintData as StockConstraintData).quantity !== undefined;
};
export interface StockConstraintData {
    quantity?: number | null;
}

export const isTransactionCountConstraintData = (constraintData: any): constraintData is TransactionCountConstraintData => {
    return constraintData && (constraintData as TransactionCountConstraintData).quantity !== undefined;
};
export interface TransactionCountConstraintData {
    quantity?: number | null;
}

export const isTransactionTypeConstraintData = (constraintData: any): constraintData is TransactionTypeConstraintData => {
    return constraintData && (constraintData as TransactionTypeConstraintData).type !== undefined;
};
export interface TransactionTypeConstraintData {
    type?: TransactionType;
}

export const isLocationConstraintData = (constraintData: any): constraintData is LocationConstraintData => {
    return constraintData && (constraintData as LocationConstraintData).min_y !== undefined && (constraintData as LocationConstraintData).max_y !== undefined;
};

export interface LocationConstraintData {
    min_y?: number | null;
    max_y?: number | null;
}

export const isProductGroupConstraintData = (constraintData: any): constraintData is ProductGroupConstraintData => {
    return constraintData && (constraintData as ProductGroupConstraintData).product_group_id !== undefined;
};
export interface ProductGroupConstraintData {
    product_group_id?: string;
}

export const isEquipmentGroupConstraintData = (constraintData: any): constraintData is EquipmentGroupConstraintData => {
    return constraintData && (constraintData as EquipmentGroupConstraintData).equipment_group_id !== undefined;
};
export interface EquipmentGroupConstraintData {
    equipment_group_id?: string;
}

export const isApplicationConstraintData = (constraintData: any): constraintData is ApplicationConstraintData => {
    return constraintData && (constraintData as ApplicationConstraintData).application !== undefined;
};
export interface ApplicationConstraintData {
    application?: string;
}

export const isDestinationConstraintData = (constraintData: any): constraintData is DestinationConstraintData => {
    return constraintData && (constraintData as DestinationConstraintData).spot_id !== undefined;
};
export interface DestinationConstraintData {
    spot_id?: string;
}

export const isFeatureConstraintData = (constraintData: any): constraintData is FeatureConstraintData => {
    return constraintData && (constraintData as FeatureConstraintData).feature !== undefined;
};
export interface FeatureConstraintData {
    feature?: string;
}

export const isFunctionalityConstraintData = (constraintData: any): constraintData is FunctionalityConstraintData => {
    return constraintData && (constraintData as FunctionalityConstraintData).functionality_id !== undefined;
};
export interface FunctionalityConstraintData {
    functionality_id?: string;
}
